import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import RelatedCities from '../../components/RelatedCities';
import { companyInfo } from '../../mockData';

const YuccaValleyPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Yucca Valley CA | Legal Document Services | Legal Document Specialist"
        description="Yucca Valley hi-desert hub process server. Central service for Morongo Basin communities, Hi-Desert Medical Center area, and rural desert properties. Expertise in remote and challenging service locations."
        keywords="process server Yucca Valley CA, legal document services Yucca Valley, court filing Yucca Valley, skip tracing Yucca Valley, San Bernardino County process server"
        canonical="https://legaldocspecialist.com/cities/yucca-valley"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Yucca Valley, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Yucca Valley, CA. Registered and bonded process servers serving the central hub of the Morongo Basin and Hi-Desert community.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Hub Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Yucca Valley Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive legal support services for the central hub of the Morongo Basin and surrounding Hi-Desert communities.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Central Hub Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Serving as the central point for Hi-Desert legal document services throughout the Morongo Basin.
              </p>
            </div>

            {/* Morongo Basin Hub */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Home size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Morongo Basin Hub Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Central Business District</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Hub for shops, services, and professional offices</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Gateway Location</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Strategic access to all Hi-Desert communities</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Residential Community Focus</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Experienced with family and residential legal matters</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Deep understanding of Yucca Valley's role as the Morongo Basin's commercial and residential center.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Access */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Yucca Valley?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Central location meets local expertise. We understand Yucca Valley's unique position as the Morongo Basin hub and community needs.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Home size={32} />,
                title: 'Basin Hub Knowledge',
                description: 'Central location with expert knowledge of all surrounding Hi-Desert communities and access routes.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Efficient Regional Service',
                description: 'Strategic location enables fast service to all Morongo Basin and Hi-Desert communities.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Community-Focused Standards',
                description: 'Registered, bonded, and experienced with the unique needs of Hi-Desert residential communities.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Yucca Valley Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Yucca Valley and the entire Morongo Basin from our central Hi-Desert location.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Morongo Basin Communities Served from Yucca Valley Hub
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Yucca Valley', 'Joshua Tree', 'Twentynine Palms', 'Morongo Valley',
                'Landers', 'Pioneertown', 'Flamingo Heights', 'Wonder Valley',
                'Johnson Valley', 'Pipes Canyon', 'Rimrock', 'Big Bear'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Complete coverage of the Morongo Basin and San Bernardino County Hi-Desert region.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Yucca Valley Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for professional service in Yucca Valley and the Morongo Basin.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Central Hi-Desert communication hub</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving the Morongo Basin Hub</strong><br />
                Central Location • Regional Access • Community Focused
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Cities Section */}
      <RelatedCities currentCity="yucca-valley" region="hi-desert" />

      <Footer />
    </div>
  );
};

export default YuccaValleyPage;